import api from './api';

export interface CancelledReceivableIncome {
  id: string;
  bookingId: string;
  customerName: string;
  project: {
    id: string;
    name: string;
  };
  unitNumber: string;
  wing: string;
  unitDisplay: string;
  totalHiddenIncomeAmount: number;
  totalReceivedAmount: number;
  finalCancelAmount: number;
  givenCancelAmount: number;
  pendingCancelAmount: number;
  cancellationDate: string;
  cancellationReason: string;
}

export interface CancelReceivableTransaction {
  id: string;
  type?: string; // 'income' or 'refund'
  transactionType?: string; // 'income' or 'refund'
  amount: number;
  date?: string; // Unified date field
  refundDate?: string; // Legacy field for refunds
  paymentMethod: string;
  referenceNumber: string;
  nameOnCheque?: string;
  remarks: string;
  processedBy: string;
}

export interface SearchReceivableParams {
  page?: number;
  limit?: number;
  projectId?: string;
  search?: string;
}

export interface AddReceivableCancelAmountData {
  bookingId: string;
  amount: number;
  date: string;
  chequeNumber?: string;
  nameOnCheque?: string;
  paymentMethod?: string;
}

class CancelReceivableIncomeService {
  private baseURL = '/cancel-receivable-income';

  async searchCancelledReceivableBookings(params: SearchReceivableParams, signal?: AbortSignal) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.projectId) queryParams.append('projectId', params.projectId);
      if (params.search) queryParams.append('search', params.search);

      const response = await api.get(
        `${this.baseURL}/cancelled-receivable-bookings?${queryParams.toString()}`,
        { signal }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error searching cancelled receivable bookings:', error);
      throw error;
    }
  }

  async getReceivableBookingTransactions(bookingId: string) {
    try {
      const response = await api.get(
        `${this.baseURL}/receivable-booking/${bookingId}/transactions`
      );
      
      return response.data;
    } catch (error) {
      console.error('Error loading receivable booking transactions:', error);
      throw error;
    }
  }

  async addReceivableCancelAmount(data: AddReceivableCancelAmountData) {
    try {
      const response = await api.post(
        `${this.baseURL}/add-receivable-cancel-amount`,
        data
      );
      
      return response.data;
    } catch (error) {
      console.error('Error adding receivable cancel amount:', error);
      throw error;
    }
  }

  // Delete a cancel receivable transaction (superadmin only)
  async deleteTransaction(transactionId: string, type: 'income' | 'refund' = 'refund') {
    try {
      const response = await api.delete(
        `${this.baseURL}/transaction/${transactionId}?type=${type}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting receivable cancel transaction:', error);
      throw error;
    }
  }
}

export const cancelReceivableIncomeService = new CancelReceivableIncomeService();